import {
  createContext,
  useContext,
  useMemo,
  FC,
  PropsWithChildren,
} from 'react'
import useMedia from 'use-media'

import { breakpoints } from 'styles/media'

type MediaQueriesMatches = {
  [key in keyof typeof breakpoints]: boolean
}

export const MediaQueryContext = createContext<MediaQueriesMatches>(null)

const mediaQueries = {
  giant: `(min-width: ${1250}px)`,
  desktop: `(min-width: ${1080}px)`,
  tablet: `(min-width: ${768}px)`,
  phone: `(min-width: ${450}px)`,
  phoneMd: `(min-width: ${375}px)`,
  sm: `(min-width: ${576}px)`,
  md: `(min-width: ${768}px)`,
  lg: `(min-width: ${992}px)`,
  xl: `(min-width: ${1200}px)`,
  xxl: `(min-width: ${1400}px)`,
  xxxl: `(min-width: ${1600}px)`,
  xwide: `(min-width: ${1920}px)`,
}

export const MediaQueryProvider: FC<PropsWithChildren> = ({ children }) => {
  // Pass `false` as defaultState so the first client render matches SSR
  // (where window.matchMedia is unavailable and use-media returns false).
  // Without this, use-media reads window.matchMedia synchronously during the
  // initial useState call, producing a tree that diverges from SSR and trips
  // React 18 hydration errors. The library's own effect updates the state to
  // the real viewport value after mount.
  const giant = useMedia(mediaQueries.giant, false)
  const desktop = useMedia(mediaQueries.desktop, false)
  const tablet = useMedia(mediaQueries.tablet, false)
  const phone = useMedia(mediaQueries.phone, false)
  const phoneMd = useMedia(mediaQueries.phoneMd, false)
  const sm = useMedia(mediaQueries.sm, false)
  const md = useMedia(mediaQueries.md, false)
  const lg = useMedia(mediaQueries.lg, false)
  const xl = useMedia(mediaQueries.xl, false)
  const xxl = useMedia(mediaQueries.xxl, false)
  const xxxl = useMedia(mediaQueries.xxxl, false)
  const xwide = useMedia(mediaQueries.xwide, false)

  const value = useMemo(
    () => ({
      giant,
      desktop,
      tablet,
      phone,
      phoneMd,
      sm,
      md,
      lg,
      xl,
      xxl,
      xxxl,
      xwide,
    }),
    [giant, desktop, tablet, phone, phoneMd, sm, md, lg, xl, xxl, xxxl, xwide],
  )

  return (
    <MediaQueryContext.Provider value={value}>
      {children}
    </MediaQueryContext.Provider>
  )
}

const defaultMockValues = {
  giant: true,
  desktop: true,
  tablet: true,
  phone: true,
  phoneMd: true,
  sm: true,
  md: true,
  lg: true,
  xl: true,
  xxl: true,
  xxxl: true,
  xwide: true,
} as const

export const mockValuesMobile: MediaQueriesMatches = {
  giant: false,
  desktop: false,
  tablet: false,
  phone: true,
  phoneMd: true,
  sm: true,
  md: false,
  lg: false,
  xl: false,
  xxl: false,
  xxxl: false,
  xwide: false,
} as const

export const MockMediaQueryProvider: FC<
  PropsWithChildren<{
    value?: MediaQueriesMatches
  }>
> = ({ value = defaultMockValues, children }) => {
  return (
    <MediaQueryContext.Provider value={value}>
      {children}
    </MediaQueryContext.Provider>
  )
}

export const useMediaQueryContext = () => useContext(MediaQueryContext)
